import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { debounceTime, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { DeleteModalComponent } from 'src/app/components/shared/dialogs/delete-modal/delete-modal.component';
import { Access, ACCESS_TYPE_LIST, API_ACCESS_LIST, ApiAccess, ReadWriteAdminType, SERVICE_ACCESS_LIST, ServiceAccess } from 'src/app/interfaces/api/access';
import { defaultUser, User } from 'src/app/interfaces/api/user';
import { AccessService } from 'src/app/services/api/accesses/access.service';
import { LabelProjectService } from 'src/app/services/api/labelProjects/labelProject.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { ButtonComponent, SearchBarComponent } from 'ui-library';

@Component({
  selector: 'app-access',
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    MatToolbarModule,
    MatTableModule,
    MatIconModule,
    MatPaginator,
    MatCheckboxModule,
    MatFormFieldModule,
    MatSort,
    ButtonComponent,
    SearchBarComponent,
    MatInputModule,
    MatDividerModule,
    MatCardModule,
    MatChipsModule,
    NoDataComponent
  ],
  standalone: true,
  templateUrl: './access.component.html',
  styleUrls: ['./access.component.scss'],
})

export class AccessComponent implements AfterViewInit, OnDestroy {
  loading: Boolean = false;
  @ViewChild('accessPaginator') accessPaginator: MatPaginator = new MatPaginator();
  @ViewChild(MatSort) sort: MatSort = new MatSort();
  accesses: any[] = [];
  accessCount: number = 0;
  accessColumns: string[] = ['name', 'author', 'actions'];
  accessData: MatTableDataSource<Access> = new MatTableDataSource();
  user: User = defaultUser;
  searchText: string = '';
  destroy$: Subject<void> = new Subject<void>();
  currentAccess: any = null;
  apiKeys = API_ACCESS_LIST;
  services = {
    dashboard: "dashboard",
    flows: "workflows",
    viewerForge: "viewer/forge",
    viewerMaps: "viewer/maps",
    planning: "planning",
    events: "events",
    risks: "risks",
    resources: "resources",
    settings: "settings"
  };
  serviceKeys = SERVICE_ACCESS_LIST;
  creatingAccess: boolean = false;
  unsavedChanges: boolean = false;
  isAdmin: boolean = false;
  requestCancel$: Subject<void> = new Subject<void>();
  searchSubject$: Subject<string> = new Subject<string>();
  lastEmptySearch: string = "";
  labelProjects: any[] = [];
  projectDisplayer: Record<string, boolean> = {};

  constructor(
    private accessService: AccessService,
    private translate: TranslateService,
    private auth: AuthService,
    public dialog: MatDialog,
    private pop: Pop,
    private labelProjectService: LabelProjectService,
  ) {
    this.user = this.auth.currentUser();
    this.isAdmin = ["dev", "admin"].includes(this.user.role.name);

    this.labelProjectService.getLabelProjects({}).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.labelProjects = res.labelProjects;
        this.labelProjects.forEach(lp => this.projectDisplayer[lp.id] = true);
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  ngAfterViewInit(): void {
    this.searchSubject$.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(text => {
      if (text !== this.searchText) {
        if (this.lastEmptySearch && text.startsWith(this.lastEmptySearch)) return;
        this.searchText = text;
        this.accessPaginator.pageIndex = 0;
        this.loadAccesses();
      }
    });

    Promise.resolve().then(() => {
      this.loadAccesses();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  search(text: string = "") {
    this.searchSubject$.next(text);
  }

  loadAccesses() {
    this.requestCancel$.next();
    this.loading = true;

    const query: any = {
      offset: this.accessPaginator ? this.accessPaginator.pageIndex * this.accessPaginator.pageSize : 0,
      limit: this.accessPaginator ? this.accessPaginator.pageSize : 10,
    };

    this.accessService.searchAndCountAccesses(this.searchText, query).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: { results: Access[], total: number }) => {
        this.lastEmptySearch = (res.total === 0 && this.searchText) ? this.searchText : "";
        this.accesses = res.results.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));

        this.accessData.data = this.accesses;
        this.accessCount = res.total;

        if (this.accessPaginator)
          this.accessPaginator.length = res.total;

        this.accessData.sort = this.sort;
        this.loading = false;
      },
      error: (err) => {
        console.error(err);
        this.loading = false;
      }
    });
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchText = filterValue.trim().toLowerCase();
    this.loadAccesses()
  }

  editAccess(access: Access) {
    if (this.currentAccess && this.currentAccess.id == access.id)
      return;

    if (this.unsavedChanges) {
      this.pop.confirm(this.translate.instant("access.confirm_close"), (confirm: any) => {
        if (confirm.ok) {
          this.unsavedChanges = false;
          this.setCurrentAccess(access);
          this.creatingAccess = false;
        }
      })
    } else {
      this.unsavedChanges = false;
      this.setCurrentAccess(access);
      this.creatingAccess = false;
    }
  }

  canEdit(access: Access) {
    if (["dev", "admin"].includes(this.user.role.name))
      return true;
    return access.author == this.user.id;

  }

  deleteAccess(access: Access) {
    const dialogRef = this.dialog.open(DeleteModalComponent, { data: { title: this.translate.instant('access.deletion'), name: `\"${access.name}\"` } });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.accessService.removeAccess(access.id).subscribe({
          next: () => {
            this.ngAfterViewInit();
            this.pop.toast(this.translate.instant("http.access.delete.success"), "primary", 4000, "delete");
          },
          error: (err) => {
            this.pop.error(this.translate.instant('http.access.delete.error', { errorCode: err.status }));
          }
        })
      }
    })
  }

  setCurrentAccess(access: Access | null = null) {
    let services: Record<ServiceAccess, boolean> = {} as any;
    let api: Record<ApiAccess, Record<ReadWriteAdminType, boolean>> = {} as any;

    SERVICE_ACCESS_LIST.forEach(key => {
      services[key] = access && access.services ? access.services[key] : true;
    });

    API_ACCESS_LIST.forEach((key: ApiAccess) => {
      api[key] = {} as any;

      ACCESS_TYPE_LIST.forEach(subKey => {
        api[key][subKey] = access && access.api && access.api[key] ? access.api[key][subKey] : (subKey == "read" ? true : false);
      });
    });

    this.currentAccess = {
      id: access ? access.id : undefined,
      name: access ? access.name : '',
      author: access?.author ?? this.user,
      services,
      api,
      adminAccess: access ? ((!access.author || Object.keys(access.author).length === 0) || !!access.adminAccess) : false,
      labelProjects: access?.labelProjects || []
    };

    Object.keys(this.projectDisplayer).forEach(projectId => {
      this.projectDisplayer[projectId] = this.currentAccess.labelProjects.includes(projectId);
    });
  }

  async addAccessForm() {
    try {
      if (this.unsavedChanges) {
        await new Promise((resolve, reject) => {
          this.pop.confirm(this.translate.instant("access.confirm_close"), (confirm: any) => {
            if (confirm.ok) {
              resolve('ok');
            } else
              reject();
          });
        });
      }
    } catch (err) {
      return;
    }
    this.creatingAccess = true;
    this.unsavedChanges = false;
    this.setCurrentAccess();
  }

  saveAccess() {
    let json = { ...this.currentAccess, author: this.currentAccess.author?.id };
    this.loading = true;

    if (this.currentAccess.id) {
      this.accessService.updateAccess(json).pipe(takeUntil(this.destroy$)).subscribe({
        next: res => {
          this.loading = false;

          let accessIndex = this.accesses.findIndex(access => access.id == this.currentAccess.id);

          if (accessIndex == -1) {
            console.error("Access index not found");
            return;
          }

          this.currentAccess.author = this.currentAccess.adminAccess ? null : this.user;
          this.accesses.splice(accessIndex, 1, this.currentAccess);
          this.currentAccess = null;
          this.accessData = new MatTableDataSource(this.accesses);
          this.unsavedChanges = false;
          this.pop.success(this.translate.instant("http.access.update.success"));
        },
        error: err => {
          if (err.status == 400 && /duplicate/.test(err.error?.message)) {
            this.pop.error(this.translate.instant('http.access.update.duplicate'));
          } else if (err.status == 400 && /no_change/.test(err.error?.message)) {
            this.pop.error(this.translate.instant('http.access.update.no_change'), "warning");
          } else
            this.pop.error(this.translate.instant('http.access.update.error', { errorCode: err.status }));

          this.loading = false;
        }
      });
    } else {
      this.accessService.addAccess(json).pipe(takeUntil(this.destroy$)).subscribe({
        next: res => {
          if (res.author)
            res.author = this.user;

          this.loading = false;
          this.currentAccess = null;
          this.creatingAccess = false;
          this.accesses.push(res);
          this.accessData = new MatTableDataSource(this.accesses);
          this.accessCount++;

          if (this.accessPaginator)
            this.accessPaginator.length = this.accessCount;

          this.unsavedChanges = false;
          this.pop.success(this.translate.instant("http.access.add.success"));
        },
        error: err => {
          if (err.status == 400 && /duplicate/.test(err.error?.message)) {
            this.pop.error(this.translate.instant('http.access.add.duplicate'));
          } else
            this.pop.error(this.translate.instant('http.access.add.error', { errorCode: err.status }));

          this.loading = false;
        }
      });
    }
  }

  close() {
    if (this.unsavedChanges) {
      this.pop.confirm(this.translate.instant("access.confirm_close"), (confirm: any) => {
        if (confirm.ok) {
          this.creatingAccess = false;
          this.currentAccess = null;
          this.unsavedChanges = false;
        }
      })
    } else {
      this.creatingAccess = false;
      this.currentAccess = null;
      this.unsavedChanges = false;
    }
  }

  updated(data: Record<string, any> = {}) {
    this.unsavedChanges = true;
    this.currentAccess.name = this.currentAccess.name.trim();

    if (data['project']) {
      let labelProjectIndex = this.currentAccess.labelProjects.findIndex((lp: any) => lp == data['project']);

      if (this.projectDisplayer[data['project']] && labelProjectIndex == -1)
        this.currentAccess.labelProjects.push(data['project']);
      else if (!this.projectDisplayer[data['project']] && labelProjectIndex >= 0)
        this.currentAccess.labelProjects.splice(labelProjectIndex, 1);
    }
  }

  canSave() {
    return !this.loading && this.unsavedChanges && this.currentAccess.name?.trim().length > 2;
  }
}
